import { Link } from "react-router-dom";
import { ScanFace, PenTool, Lock, ArrowRight, Zap, Clock, Calculator } from "lucide-react";
import { SimulatorCard } from "@/components/SimulatorCard";
import { MobileActionBar } from "@/components/MobileActionBar";
import { Button } from "@/components/ui/button";
import { MARKET_SPEED } from "@/lib/comparison";

const TRUST = [
  { icon: ScanFace, t: "Biometría certificada" },
  { icon: PenTool, t: "Firma Ley 19.799" },
  { icon: Lock, t: "Datos Ley 21.719" },
];

export const HeroSection = ({ login, user }) => (
  <section className="relative overflow-hidden bg-[#13315C] text-white pt-28 pb-20 md:pt-36 md:pb-28">
    {/* Profundidad sin foto: glows + grano + grilla sutil */}
    <div className="absolute -top-32 -right-24 h-[28rem] w-[28rem] rounded-full bg-[#F5B301]/15 blur-[120px]" />
    <div className="absolute -bottom-40 -left-24 h-[26rem] w-[26rem] rounded-full bg-[#1F9BD7]/20 blur-[120px]" />
    <div className="absolute inset-0 tf-grain opacity-[0.18]" />
    <div
      className="absolute inset-0 opacity-[0.06]"
      style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "56px 56px" }}
    />

    <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
      <div>
        <div
          className="tf-rise inline-flex items-center gap-2 rounded-full border border-[#F5B301]/40 bg-[#F5B301]/10 px-3 py-1.5 text-xs font-bold text-[#F5B301] mb-6"
          data-testid="hero-72h-badge"
        >
          <Zap size={14} /> TRANSFERENCIA EXPRÉS · 72 HORAS
        </div>

        <h1
          className="tf-rise font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" style={{ animationDelay: "0.05s" }}
        >
          Tu transferencia vehicular lista en <span className="text-[#F5B301]">72 horas</span>, no en semanas
        </h1>

        <p
          className="tf-rise mt-6 text-lg text-white/70 max-w-xl leading-relaxed" style={{ animationDelay: "0.15s" }}
        >
          100% digital, segura y sin filas. Mientras otras plataformas tardan semanas, Transfiérelo
          completa tu expediente —validación, firma e informe— y lo deja listo para inscripción en 72 horas.
        </p>

        <div
          className="tf-rise mt-8 flex flex-wrap gap-3" style={{ animationDelay: "0.25s" }}
        >
          <Button onClick={user ? undefined : login} asChild={!!user} className="bg-[#F5B301] hover:bg-[#dca400] text-[#13315C] rounded-md h-12 px-6 font-semibold text-base group" data-testid="hero-start-btn">
            {user ? <Link to="/dashboard">Ir a mi panel <ArrowRight size={18} className="ml-1 group-hover:translate-x-1 transition-transform" /></Link>
                  : <span>Comenzar ahora <ArrowRight size={18} className="ml-1 inline group-hover:translate-x-1 transition-transform" /></span>}
          </Button>
          <Button asChild variant="outline" className="rounded-md h-12 px-6 font-semibold text-base bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">
            <Link to="/quienes-somos" data-testid="hero-about-btn">Por qué elegirnos</Link>
          </Button>
        </div>

        {/* Franja comparativa de velocidad */}
        <div
          className="tf-rise mt-9 rounded-xl border border-white/15 bg-white/[0.06] backdrop-blur p-4" style={{ animationDelay: "0.35s" }}
          data-testid="hero-speed-band"
        >
          <p className="text-[11px] font-bold uppercase tracking-wider text-white/50 mb-3 flex items-center gap-1.5">
            <Clock size={13} /> Tiempo hasta dejar tu transferencia lista
          </p>
          <div className="space-y-2.5">
            {MARKET_SPEED.map((m) => (
              <div key={m.name} className="flex items-center gap-3">
                <span className={`text-sm flex-1 ${m.highlight ? "font-bold text-white" : "text-white/60"}`}>{m.name}</span>
                <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className={`h-full rounded-full ${m.highlight ? "bg-[#F5B301] w-[12%]" : "bg-white/25"}`} style={{ width: m.highlight ? "12%" : m.time.includes("30") ? "100%" : "62%" }} />
                </div>
                <span className={`text-sm w-28 text-right ${m.highlight ? "font-bold text-[#F5B301]" : "text-white/60"}`}>{m.time}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-white/60 mt-3">* Tiempos según información pública del mercado; la inscripción definitiva depende del Registro Civil.</p>
        </div>

        <div
          className="tf-rise mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm" style={{ animationDelay: "0.45s" }}
        >
          {TRUST.map((c) => (
            <div key={c.t} className="flex items-center gap-2 text-white/80">
              <c.icon size={16} className="text-[#1F9BD7]" /> {c.t}
            </div>
          ))}
        </div>
      </div>

      <div className="tf-rise scroll-mt-24" id="simulador" style={{ animationDelay: "0.2s" }}>
        <SimulatorCard />
      </div>
    </div>
    <MobileActionBar hideWhenVisible="#simulador" testid="hero-mobile-bar">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold text-[#13315C] leading-tight">Transferencia lista en 72 h</p>
          <p className="text-[11px] text-slate-500 leading-tight">Simula gratis en 10 segundos</p>
        </div>
        <Button onClick={() => document.getElementById("simulador")?.scrollIntoView({ behavior: "smooth", block: "start" })}
          className="h-11 px-5 bg-[#F5B301] hover:bg-[#dca400] text-[#13315C] rounded-md font-semibold shrink-0" data-testid="hero-mobile-bar-btn">
          <Calculator size={16} className="mr-1.5" /> Calcular costo
        </Button>
      </div>
    </MobileActionBar>
  </section>
);
